const CCG_SHOP_DOMAIN = 'iqttbw-cc.myshopify.com';
const CCG_STOREFRONT_TOKEN = 'ea27989ec24668b73163dde292053ad7';

const CCG_STOREFRONT_ENDPOINT =
  `https://${CCG_SHOP_DOMAIN}/api/2026-07/graphql.json`;

  // ============================================================
// CCG Unlimited Filters
// GradeSubject -> complete Unit list
// GradeSubject / Unit -> complete Topic list
// ============================================================

const CCG_GRADE_PARAM = 'filter.p.m.custom.gradesubject';
const CCG_UNIT_PARAM = 'filter.p.m.custom.unit';
const CCG_TOPIC_PARAM = 'filter.p.m.custom.topic';

const ccgFilterCache = new Map();

let ccgObserver = null;
let ccgObserverRestartTimer = null;

function ccgStartObserver() {
  if (!ccgObserver) return;

  ccgObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

function ccgGetSearchTerm() {
  const url = new URL(window.location.href);
  return url.searchParams.get('q')?.trim() || '*';
}

function ccgPauseObserver() {
  if (!ccgObserver) return;

  ccgObserver.disconnect();

  clearTimeout(ccgObserverRestartTimer);

  ccgObserverRestartTimer = setTimeout(() => {
    ccgStartObserver();
  }, 50);
}

function ccgGetSelectedValues(paramName) {
  return new URL(window.location.href).searchParams.getAll(paramName);
}

async function ccgStorefrontRequest(query, variables = {}) {
  const response = await fetch(CCG_STOREFRONT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': CCG_STOREFRONT_TOKEN
    },
    body: JSON.stringify({
      query,
      variables
    })
  });

  if (!response.ok) {
    throw new Error(`Storefront API HTTP error: ${response.status}`);
  }

  const payload = await response.json();

  if (payload.errors) {
    console.error('CCG Storefront API errors:', payload.errors);
    throw new Error(
      payload.errors.map(error => error.message).join('; ')
    );
  }

  return payload.data;
}

function ccgFindFilterDetails(label) {
  return Array.from(
    document.querySelectorAll(
      'details[data-ccg-filter-label][data-ccg-param-name]'
    )
  ).find(details =>
    details.dataset.ccgFilterLabel?.trim().toLowerCase() ===
    label.toLowerCase()
  );
}

function ccgSortValues(values) {
  return values.sort((a, b) =>
    a.value.localeCompare(b.value, undefined, {
      sensitivity: 'base',
      numeric: true
    })
  );
}

// ============================================================
// CCG Grade & Subject display order
// ============================================================

const CCG_GRADE_SUBJECT_ORDER = [
  // Elementary
  'Elementary Sex Education',

  'Grade 3 Language Arts',
  'Grade 3 Math',
  'Grade 3 Science',
  'Grade 3 Social Studies',

  'Grade 4 Language Arts',
  'Grade 4 Math',
  'Grade 4 Science',
  'Grade 4 Social Studies',

  'Grade 5 Language Arts',
  'Grade 5 Math',
  'Grade 5 Science',
  'Grade 5 Social Studies',

  // Middle School
  'Middle School Sex Education',

  'Grade 6 Language Arts',
  'Grade 6 Math',
  'Grade 6 Science',
  'Grade 6 Social Studies',

  'Grade 7 Language Arts',
  'Grade 7 Math',
  'Grade 7 Science',
  'Grade 7 Social Studies',

  'Grade 8 Language Arts',
  'Grade 8 Math',
  'Grade 8 Science',
  'Grade 8 Social Studies',

  'Middle School French Sentences',
  'Middle School French Vocabulary',
  'Middle School French Verbs',
  'Middle School Spanish Sentences',
  'Middle School Spanish Vocabulary',
  'Middle School Spanish Verbs',

  // High School
  'High School Sex Education',

  'Grade 9 Language Arts',
  'Grade 10 Language Arts',
  'Grade 11 Language Arts',

  'Grade 9 Math',
  'High School Algebra 1',
  'High School Geometry',
  'High School Algebra 2',
  'High School PreCalculus',
  'High School Statistics',
  'High School SAT Prep',

  'Grade 9 Science',
  'High School Biology',
  'High School Chemistry',
  'High School Physics',
  'High School AP Biology',
  'High School Earth Science',
  'High School Astronomy',

  'High School Civics',
  'High School Economics',
  'High School U.S. History',
  'High School World History'
];

function ccgOrderGradeSubjects() {
  const detailsList = Array.from(
    document.querySelectorAll(
      'details[data-ccg-filter-label][data-ccg-param-name]'
    )
  ).filter(details => {
    const label =
      details.dataset.ccgFilterLabel?.trim().toLowerCase();

    return (
      label === 'grade & subject' ||
      label === 'gradesubject'
    );
  });

  if (!detailsList.length) return;

  const order = new Map(
    CCG_GRADE_SUBJECT_ORDER.map((value, index) => [
      value,
      index
    ])
  );

  ccgPauseObserver();

  detailsList.forEach(details => {
    const list =
      details.querySelector('[data-ccg-filter-values]');

    if (!list) return;

    const items = Array.from(list.children);

    items.sort((a, b) => {
      const aValue =
        a.querySelector('input[type="checkbox"]')?.value || '';

      const bValue =
        b.querySelector('input[type="checkbox"]')?.value || '';

      const aRank = order.has(aValue)
        ? order.get(aValue)
        : Number.MAX_SAFE_INTEGER;

      const bRank = order.has(bValue)
        ? order.get(bValue)
        : Number.MAX_SAFE_INTEGER;

      if (aRank !== bRank) {
        return aRank - bRank;
      }

      return aValue.localeCompare(bValue, undefined, {
        sensitivity: 'base',
        numeric: true
      });
    });

    items.forEach(item => list.appendChild(item));
  });
}

function ccgKeepSelectedValuesVisible(values, paramName) {
  const selectedValues = ccgGetSelectedValues(paramName);

  selectedValues.forEach(selectedValue => {
    const alreadyExists = values.some(
      item => item.value === selectedValue
    );

    if (!alreadyExists) {
      values.push({
        value: selectedValue,
        count: 0
      });
    }
  });

  return ccgSortValues(values);
}

function ccgMergeValueLists(lists) {
  const counts = new Map();

  lists.flat().forEach(item => {
    if (!item?.value) return;

    counts.set(
      item.value,
      (counts.get(item.value) || 0) + (item.count || 0)
    );
  });

  return ccgSortValues(
    Array.from(counts.entries()).map(([value, count]) => ({
      value,
      count
    }))
  );
}

async function ccgFetchCombinedValues({
  targetKey,
  gradeSubjects = [],
  units = []
}) {
  const requests = [];

  // GradeSubject + Unit selected:
  // fetch every applicable combination and combine them.
  if (gradeSubjects.length && units.length) {
    gradeSubjects.forEach(gradeSubject => {
      units.forEach(unit => {
        requests.push(
          ccgFetchMetafieldValues({
            targetKey,
            gradeSubject,
            unit
          })
        );
      });
    });
  }

  // GradeSubject selected, no Unit selected.
  else if (gradeSubjects.length) {
    gradeSubjects.forEach(gradeSubject => {
      requests.push(
        ccgFetchMetafieldValues({
          targetKey,
          gradeSubject
        })
      );
    });
  }

  // Unit selected without a GradeSubject.
  else if (units.length) {
    units.forEach(unit => {
      requests.push(
        ccgFetchMetafieldValues({
          targetKey,
          unit
        })
      );
    });
  }

  if (!requests.length) {
    return [];
  }

  const lists = await Promise.all(requests);

  return ccgMergeValueLists(lists);
}

function ccgBuildProductFilters({ gradeSubject, unit }) {
  const filters = [];

  if (gradeSubject) {
    filters.push({
      productMetafield: {
        namespace: 'custom',
        key: 'gradesubject',
        value: gradeSubject
      }
    });
  }

  if (unit) {
    filters.push({
      productMetafield: {
        namespace: 'custom',
        key: 'unit',
        value: unit
      }
    });
  }

  return filters;
}

async function ccgFetchMetafieldValues({
  targetKey,
  gradeSubject = null,
  unit = null
}) {
  const searchQuery = ccgGetSearchTerm();

  const cacheKey = JSON.stringify({
    targetKey,
    gradeSubject,
    unit,
    searchQuery
  });

  if (ccgFilterCache.has(cacheKey)) {
    return ccgFilterCache.get(cacheKey);
  }

  const query = `
    query CCGFilterValues(
      $first: Int!
      $after: String
      $filters: [ProductFilter!]
      $targetKey: String!
      $searchQuery: String!
    ) {
      search(
        first: $first
        after: $after
        query: $searchQuery
        types: [PRODUCT]
        productFilters: $filters
        unavailableProducts: HIDE
      ) {
        nodes {
          ... on Product {
            target: metafield(
              namespace: "custom"
              key: $targetKey
            ) {
              value
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const filters = ccgBuildProductFilters({
    gradeSubject,
    unit
  });

  const counts = new Map();

  let after = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const data = await ccgStorefrontRequest(query, {
      first: 250,
      after,
      filters,
      targetKey,
      searchQuery
    });

    const connection = data.search;

    for (const node of connection.nodes) {
      const value = node.target?.value?.trim();

      if (!value) continue;

      counts.set(
        value,
        (counts.get(value) || 0) + 1
      );
    }

    hasNextPage = connection.pageInfo.hasNextPage;
    after = connection.pageInfo.endCursor;
  }

  const values = ccgSortValues(
    Array.from(counts.entries()).map(([value, count]) => ({
      value,
      count
    }))
  );

  ccgFilterCache.set(cacheKey, values);

  return values;
}

function ccgRenderFilterList({
  details,
  values,
  selectedParam,
  idPrefix
}) {
    ccgPauseObserver();

  const list = details.querySelector('[data-ccg-filter-values]');

  if (!list) {
    console.warn(`CCG: value list not found for ${idPrefix}`);
    return;
  }

  const selectedValues = new Set(
    ccgGetSelectedValues(selectedParam)
  );

  const paramName =
    details.dataset.ccgParamName || selectedParam;

  list.innerHTML = '';

  values.forEach((entry, index) => {
    const item = document.createElement('li');
    item.className = 'flex items-center gap-3';

    const input = document.createElement('input');
    input.name = paramName;
    input.value = entry.value;
    input.className = 'checkbox';
    input.type = 'checkbox';

    const id = `${idPrefix}-${index}-${entry.value}`
      .replace(/[^a-zA-Z0-9_-]/g, '-');

    input.id = id;
    input.checked = selectedValues.has(entry.value);

    const label = document.createElement('label');
    label.htmlFor = id;
    label.className = 'flex items-center gap-1';

    label.appendChild(
      document.createTextNode(entry.value)
    );

    const count = document.createElement('span');
    count.className = input.checked
      ? 'text-sm'
      : 'text-sm text-subtext';
    count.textContent = `(${entry.count})`;

    label.appendChild(count);

    item.appendChild(input);
    item.appendChild(label);
    list.appendChild(item);
  });

  const showMore = details.querySelector('show-more-button');

  if (showMore) {
    showMore.hidden = true;
  }
}

async function ccgPopulateUnits() {
  const selectedGrades =
    ccgGetSelectedValues(CCG_GRADE_PARAM);

  if (selectedGrades.length === 0) {
    return;
  }

  const unitDetails =
    ccgFindFilterDetails('Unit');

  if (!unitDetails) {
    return;
  }

  try {
    let units = await ccgFetchCombinedValues({
      targetKey: 'unit',
      gradeSubjects: selectedGrades
    });

    units = ccgKeepSelectedValuesVisible(
      units,
      CCG_UNIT_PARAM
    );

    ccgRenderFilterList({
      details: unitDetails,
      values: units,
      selectedParam: CCG_UNIT_PARAM,
      idPrefix: 'CCG-Unit'
    });

    console.log(
      `CCG: loaded ${units.length} combined Units`,
      selectedGrades
    );
  } catch (error) {
    console.error(
      'CCG: unable to build Unit list:',
      error
    );
  }
}

function ccgEnsureTopicDetails() {
  let topicDetails =
    ccgFindFilterDetails('Topic');

  if (topicDetails) {
    return topicDetails;
  }

  const unitDetails =
    ccgFindFilterDetails('Unit');

  if (!unitDetails) {
    return null;
  }

  topicDetails =
    unitDetails.cloneNode(true);

  topicDetails.removeAttribute('open');

  topicDetails.dataset.ccgFilterLabel =
    'Topic';

  topicDetails.dataset.ccgParamName =
    CCG_TOPIC_PARAM;

  topicDetails.dataset.index =
    'ccg-custom-topic';

  const heading =
    topicDetails.querySelector(
      '.accordion-details__summary .h4'
    );

  if (heading) {
    heading.textContent = 'Topic';
  }

  const list =
    topicDetails.querySelector(
      '[data-ccg-filter-values]'
    );

  if (list) {
    list.innerHTML = '';
  }

  const showMore =
    topicDetails.querySelector(
      'show-more-button'
    );

  if (showMore) {
    showMore.remove();
  }

  unitDetails.insertAdjacentElement(
    'afterend',
    topicDetails
  );

  return topicDetails;
}

async function ccgPopulateTopics() {
  const selectedGrades =
    ccgGetSelectedValues(CCG_GRADE_PARAM);

  const selectedUnits =
    ccgGetSelectedValues(CCG_UNIT_PARAM);

  if (
    selectedGrades.length === 0 &&
    selectedUnits.length === 0
  ) {
    return;
  }

  const topicDetails =
    ccgEnsureTopicDetails();

  if (!topicDetails) {
    return;
  }

  try {
    let topics = await ccgFetchCombinedValues({
      targetKey: 'topic',
      gradeSubjects: selectedGrades,
      units: selectedUnits
    });

    topics = ccgKeepSelectedValuesVisible(
      topics,
      CCG_TOPIC_PARAM
    );

    ccgRenderFilterList({
      details: topicDetails,
      values: topics,
      selectedParam: CCG_TOPIC_PARAM,
      idPrefix: 'CCG-Topic'
    });

    console.log(
      `CCG: loaded ${topics.length} combined Topics`,
      {
        selectedGrades,
        selectedUnits
      }
    );
  } catch (error) {
    console.error(
      'CCG: unable to build Topic list:',
      error
    );
  }
}

// ============================================================
// SEARCH-AWARE COUNTS FOR NATIVE UPPER FILTERS
// Product Type -> School Level -> GradeSubject
//
// Runs ONLY when a text search is active.
// Does not replace Shopify checkboxes or selection behavior.
// It changes only the displayed numbers.
// ============================================================

function ccgParseMetafieldParam(paramName) {
  const prefix = 'filter.p.m.';

  if (!paramName?.startsWith(prefix)) {
    return null;
  }

  const remainder = paramName.slice(prefix.length);
  const dot = remainder.indexOf('.');

  if (dot === -1) {
    return null;
  }

  return {
    namespace: remainder.slice(0, dot),
    key: remainder.slice(dot + 1)
  };
}

function ccgGetDetailsParam(label) {
  return ccgFindFilterDetails(label)?.dataset.ccgParamName || null;
}

function ccgGetSelectedForDetails(label) {
  const paramName = ccgGetDetailsParam(label);

  if (!paramName) {
    return [];
  }

  return ccgGetSelectedValues(paramName);
}

async function ccgFetchUpperCountProducts({
  productType = null,
  schoolLevel = null
} = {}) {
  const searchQuery = ccgGetSearchTerm();

  const schoolParam =
    ccgGetDetailsParam('School Level');

  const gradeParam =
    ccgGetDetailsParam('Grade & Subject') ||
    ccgGetDetailsParam('GradeSubject');

  const schoolMeta =
    ccgParseMetafieldParam(schoolParam);

  const gradeMeta =
    ccgParseMetafieldParam(gradeParam);

  if (!schoolMeta || !gradeMeta) {
    return [];
  }

  const filters = [];

  if (productType) {
    filters.push({
      productType
    });
  }

  if (schoolLevel) {
    filters.push({
      productMetafield: {
        namespace: schoolMeta.namespace,
        key: schoolMeta.key,
        value: schoolLevel
      }
    });
  }

  const cacheKey = JSON.stringify({
    type: 'upper-count-products',
    searchQuery,
    productType,
    schoolLevel
  });

  if (ccgFilterCache.has(cacheKey)) {
    return ccgFilterCache.get(cacheKey);
  }

  const query = `
    query CCGUpperCounts(
      $first: Int!
      $after: String
      $filters: [ProductFilter!]
      $searchQuery: String!
      $schoolNamespace: String!
      $schoolKey: String!
      $gradeNamespace: String!
      $gradeKey: String!
    ) {
      search(
        first: $first
        after: $after
        query: $searchQuery
        types: [PRODUCT]
        productFilters: $filters
        unavailableProducts: HIDE
      ) {
        nodes {
          ... on Product {
            productType

            schoolLevel: metafield(
              namespace: $schoolNamespace
              key: $schoolKey
            ) {
              value
            }

            gradeSubject: metafield(
              namespace: $gradeNamespace
              key: $gradeKey
            ) {
              value
            }
          }
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const products = [];

  let after = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const data = await ccgStorefrontRequest(query, {
      first: 250,
      after,
      filters,
      searchQuery,
      schoolNamespace: schoolMeta.namespace,
      schoolKey: schoolMeta.key,
      gradeNamespace: gradeMeta.namespace,
      gradeKey: gradeMeta.key
    });

    products.push(...data.search.nodes);

    hasNextPage = data.search.pageInfo.hasNextPage;
    after = data.search.pageInfo.endCursor;
  }

  ccgFilterCache.set(cacheKey, products);

  return products;
}

function ccgCountUpperField(products, fieldName) {
  const counts = new Map();

  products.forEach(product => {
    let value = null;

    if (fieldName === 'productType') {
      value = product.productType?.trim();
    } else {
      value = product[fieldName]?.value?.trim();
    }

    if (!value) return;

    counts.set(
      value,
      (counts.get(value) || 0) + 1
    );
  });

  return counts;
}

function ccgApplyCountsToNativeFilter(label, counts) {
  const details =
    ccgFindFilterDetails(label);

  if (!details) return;

  ccgPauseObserver();

  details
    .querySelectorAll('input[type="checkbox"]')
    .forEach(input => {
      const count =
        counts.get(input.value) || 0;

      const optionLabel = details.querySelector(
        `label[for="${CSS.escape(input.id)}"]`
      );

      if (!optionLabel) return;

      const countSpan =
        optionLabel.querySelector('.text-sm');

      if (countSpan) {
        countSpan.textContent = `(${count})`;
      }
    });
}

async function ccgUpdateSearchUpperCounts() {
  // No text search = leave Shopify's normal counts alone.
  if (ccgGetSearchTerm() === '*') {
    return;
  }

  try {
    // --------------------------------------------------------
    // PRODUCT TYPE
    // Search only.
    // --------------------------------------------------------

    const productTypeProducts =
      await ccgFetchUpperCountProducts();

    const productTypeCounts =
      ccgCountUpperField(
        productTypeProducts,
        'productType'
      );

    ccgApplyCountsToNativeFilter(
      'Product Type',
      productTypeCounts
    );


    // --------------------------------------------------------
    // SCHOOL LEVEL
    // Search + selected Product Type(s)
    // --------------------------------------------------------

    const selectedProductTypes =
      ccgGetSelectedForDetails('Product Type');

    const schoolRequests =
      selectedProductTypes.length
        ? selectedProductTypes.map(productType =>
            ccgFetchUpperCountProducts({
              productType
            })
          )
        : [
            ccgFetchUpperCountProducts()
          ];

    const schoolProductLists =
      await Promise.all(schoolRequests);

    const schoolCounts =
      ccgCountUpperField(
        schoolProductLists.flat(),
        'schoolLevel'
      );

    ccgApplyCountsToNativeFilter(
      'School Level',
      schoolCounts
    );


    // --------------------------------------------------------
    // GRADE SUBJECT
    // Search + Product Type + School Level
    // --------------------------------------------------------

    const selectedSchools =
      ccgGetSelectedForDetails('School Level');

    const productTypes =
      selectedProductTypes.length
        ? selectedProductTypes
        : [null];

    const schools =
      selectedSchools.length
        ? selectedSchools
        : [null];

    const gradeRequests = [];

    productTypes.forEach(productType => {
      schools.forEach(schoolLevel => {
        gradeRequests.push(
          ccgFetchUpperCountProducts({
            productType,
            schoolLevel
          })
        );
      });
    });

    const gradeProductLists =
      await Promise.all(gradeRequests);

    const gradeCounts =
      ccgCountUpperField(
        gradeProductLists.flat(),
        'gradeSubject'
      );

    ccgApplyCountsToNativeFilter(
      'Grade & Subject',
      gradeCounts
    );

  } catch (error) {
    console.error(
      'CCG: unable to update search upper counts:',
      error
    );
  }
}

async function ccgPopulateUnlimitedFilters() {
  await Promise.all([
    ccgPopulateUnits(),
    ccgPopulateTopics(),
    ccgUpdateSearchUpperCounts()
  ]);

  ccgOrderGradeSubjects();
}

function ccgInitializeUnlimitedFilters() {
  ccgPopulateUnlimitedFilters();

ccgObserver = new MutationObserver(() => {
  window.clearTimeout(
    ccgInitializeUnlimitedFilters.timer
  );

  ccgInitializeUnlimitedFilters.timer =
    window.setTimeout(() => {
      ccgPopulateUnlimitedFilters();
    }, 150);
});

ccgStartObserver();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    ccgInitializeUnlimitedFilters
  );
} else {
  ccgInitializeUnlimitedFilters();
}